import { useCallback, useEffect, useState } from 'react';

export class PreloadImageError extends Error {
  constructor(src: string) {
    super(`Failed to load image from ${src}`);
    this.name = 'PreloadImageError';
  }
}

interface IUsePreloadImageResult {
  imgSrc: string;
  isLoading: boolean;
  error: PreloadImageError | undefined;
  reload: () => void;
}

// Since hooks cannot be conditional, we need to handle undefined srcs
type TImageErrorCtor = new (src: string) => Error;

function usePreloadImage(
  src: string | undefined,
  errorClass: TImageErrorCtor = PreloadImageError,
): IUsePreloadImageResult {
  const [isLoading, setIsLoading] = useState(true);
  const [cacheBustedSrc, setCacheBustedSrc] = useState<string>('');
  const [error, setError] = useState<PreloadImageError | undefined>(undefined);
  // Key to trigger reloads (incremented on each reload)
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => {
    // Increment the reload key to trigger a new cachebuster
    setReloadKey((prevKey) => prevKey + 1);
  }, []);

  useEffect(
    () => {
      // Prevent running effect if no src is provided
      if (!src) {
        setError(undefined);
        setCacheBustedSrc('');
        setIsLoading(true);
        return;
      }
      setError(undefined);

      // Function to generate the cachebuster URL based on current date and time
      const getCacheBustedUrl = (url: string): string => {
        // Use the current timestamp as the cachebuster
        const timestamp = Date.now();
        return `${url}?cb=${timestamp}`;
      };

      const img = new Image();
      const bustedSrc = getCacheBustedUrl(src);
      // Update the image source with the cachebuster
      setCacheBustedSrc(bustedSrc);
      setIsLoading(true);

      // Set image source and stop loading when the image is fully loaded
      img.src = bustedSrc;
      img.onload = (): void => {
        setIsLoading(false);
        setError(undefined);
      };

      img.onerror = (): void => {
        setIsLoading(false);
        const err = new errorClass(src);
        setError(err);
      };
    },
    // Re-run effect whenever the source or reloadKey changes
    [src, reloadKey],
  );

  return { isLoading, imgSrc: cacheBustedSrc, error, reload };
}

export default usePreloadImage;
